import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import SourceRoundedIcon from "@mui/icons-material/SourceRounded";
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getMe } from "../../api/users";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import { clearTemplateReloginGate } from "../../templateReloginGate";

const DRAWER_WIDTH = 280;

/** 今日（JST）のワークスケジュール実行画面へ。UseTodo の scheduledOn と同じ YYYY-MM-DD。 */
function getTodayTodoPath() {
  const ymd = new Date().toLocaleDateString("en-CA", {
    timeZone: "Asia/Tokyo",
  });
  return `/todos/by-date/${ymd}`;
}

function getNavigationItems({ checkListEnabled, assigneeSelectionEnabled }) {
  return [
    { label: "ホーム", path: "/", icon: <HomeRoundedIcon /> },
    ...(assigneeSelectionEnabled
      ? [
          {
            label: "クルー管理",
            path: "/edit-staff/",
            icon: <PeopleAltRoundedIcon />,
            matchPrefix: "/edit-staff",
          },
        ]
      : []),
    {
      label: "テンプレート",
      path: "/templates",
      icon: <SourceRoundedIcon />,
      matchPrefix: "/templates",
    },
    {
      label: "ワークスケジュール",
      path: getTodayTodoPath(),
      icon: <ListAltRoundedIcon />,
      matchPrefix: "/todos",
    },
    ...(checkListEnabled
      ? [
          {
            label: "チェックリスト",
            path: "/checklists",
            icon: <PlaylistAddCheckRoundedIcon />,
            matchPrefix: "/checklists",
          },
        ]
      : []),
    {
      label: "ダッシュボード",
      path: "/dashboard",
      icon: <DashboardRoundedIcon />,
      matchPrefix: "/dashboard",
    },
  ];
}

function getPageMeta(pathname, search) {
  if (pathname === "/") {
    return {
      title: "ホーム",
    };
  }
  if (pathname.startsWith("/dashboard")) {
    return {
      title: "ダッシュボード",
    };
  }
  if (pathname === "/templates/new") {
    return {
      title: "テンプレート作成",
    };
  }
  if (/^\/templates\/[^/]+\/edit$/.test(pathname)) {
    return {
      title: "テンプレート編集",
    };
  }
  if (pathname.startsWith("/templates/settings")) {
    return {
      title: "自動生成テンプレート設定",
    };
  }
  if (pathname.startsWith("/templates/select-base-template")) {
    const params = new URLSearchParams(search || "");
    const mode = params.get("mode") === "edit" ? "edit" : "duplicate";
    return {
      title:
        mode === "edit"
          ? "編集するテンプレートを選択"
          : "複製の元テンプレートを選択",
    };
  }
  if (pathname.startsWith("/templates/select-template")) {
    return {
      title: "テンプレートからワースケ等を作成",
    };
  }
  if (pathname.startsWith("/templates")) {
    return {
      title: "テンプレート",
    };
  }
  if (pathname.startsWith("/todos/by-date/")) {
    return {
      title: "ワースケ実行",
    };
  }
  if (/^\/todos\/[^/]+$/.test(pathname)) {
    return {
      title: "ワースケ実行",
    };
  }
  if (pathname.startsWith("/todos/")) {
    return {
      title: "ワークスケジュール",
    };
  }
  if (pathname.startsWith("/checklists/") && pathname !== "/checklists") {
    return {
      title: "チェックリスト実行",
    };
  }
  if (pathname.startsWith("/checklists")) {
    return {
      title: "チェックリスト",
    };
  }
  if (pathname.startsWith("/edit-staff")) {
    return {
      title: "クルー管理",
    };
  }
  if (pathname === "/account/change-password") {
    return {
      title: "パスワード変更",
    };
  }
  if (pathname.startsWith("/account")) {
    return {
      title: "アカウント設定",
    };
  }
  return {
    title: "ホーム",
  };
}

function isCurrentPath(pathname, item) {
  if (item.path === "/") return pathname === "/";
  return pathname.startsWith(item.matchPrefix || item.path);
}

function NavigationContent({ items, pathname, onNavigate, onSignOut }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 2,
      }}
    >
      <List sx={{ flexGrow: 1 }}>
        {items.map((item) => {
          const selected = isCurrentPath(pathname, item);
          return (
            <ListItemButton
              key={item.label}
              selected={selected}
              onClick={() => onNavigate(item.path)}
              sx={{
                mb: 0.5,
                minHeight: 44,
                border: selected
                  ? `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
                  : "1px solid transparent",
                backgroundColor: selected
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <List disablePadding>
        <ListItemButton
          onClick={onSignOut}
          sx={{
            mb: 0.5,
            minHeight: 44,
            color: "error.main",
            border: "1px solid transparent",
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <LogoutRoundedIcon />
          </ListItemIcon>
          <ListItemText primary="ログアウト" />
        </ListItemButton>
      </List>
    </Box>
  );
}

function AppShell() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUserKey, signOut } = useAuth();
  const config = useConfig();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [me, setMe] = useState(null);
  const pageMeta = getPageMeta(location.pathname, location.search);

  const navigationItems = useMemo(
    () =>
      getNavigationItems({
        checkListEnabled: Boolean(config?.features?.enableCheckList),
        assigneeSelectionEnabled: Boolean(
          config?.features?.enableAssigneeSelection
        ),
      }),
    [
      config?.features?.enableAssigneeSelection,
      config?.features?.enableCheckList,
    ]
  );

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleSignOut = () => {
    signOut();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    if (!currentUserKey) {
      setMe(null);
      return () => {
        cancelled = true;
      };
    }

    const fetchMe = async () => {
      try {
        const data = await getMe();
        if (!cancelled) setMe(data);
      } catch (e) {
        if (!cancelled) setMe(null);
      }
    };

    fetchMe();

    const handleProfileUpdate = () => fetchMe();
    window.addEventListener("user-profile-updated", handleProfileUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("user-profile-updated", handleProfileUpdate);
    };
  }, [currentUserKey]);

  useEffect(() => {
    if (!location.pathname.startsWith("/templates")) {
      clearTemplateReloginGate();
    }
  }, [location.pathname]);

  const drawer = (
    <NavigationContent
      items={navigationItems}
      pathname={location.pathname}
      onNavigate={handleNavigate}
      onSignOut={handleSignOut}
    />
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        color="inherit"
        sx={{
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { lg: `${DRAWER_WIDTH}px` },
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: "blur(14px)",
        }}
      >
        <Toolbar sx={{ minHeight: 72 }}>
          {!isDesktop && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}
          <Typography variant="h6">{pageMeta.title}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {me && (
            <Typography
              variant="body2"
              onClick={() => navigate("/account")}
              sx={{
                color: "text.secondary",
                maxWidth: { xs: 180, sm: 260, md: 360 },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                  textDecoration: "underline",
                },
              }}
              title={me.display_name || "アカウント名が設定されていません"}
            >
              {me.display_name || "アカウント名が設定されていません"}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}
      >
        <Drawer
          variant={isDesktop ? "permanent" : "temporary"}
          open={isDesktop ? true : mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: "block",
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: 72 }} />
        <Box
          sx={{
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 2, sm: 3 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default AppShell;
